import { FC, useContext } from "react";
import { Box, Flex, Text, useMantineTheme } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { PlayerName } from "../../../../Components/PlayerName";
import { CompetitionContext } from "../../../../CompetitionContext";
import { usePlayer } from "../../../../hooks/usePlayer";
import { useCompetition } from "../../../../hooks/useCompetition";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { IconBallFootball, IconTrophy } from "@tabler/icons-react";
import { useTopscorer } from "../../../../hooks/useTopscorer";
import { CenteredLoader } from "../../../../Components/CenteredLoader";
import { CardContainer } from "../../../../Components/CardContainer";
import { WidgetHeader } from "../../../../Components/WidgetHeader";
import { PlayerNumber } from "../../../../Components/PlayerNumber.tsx";

interface TopscorerHighlightProps {
  maxPlayers?: number;
}

export const TopscorerCard: FC<{
  playerId: string;
  goals: number;
  assists: number;
  position: number;
  showAssists: boolean;
}> = ({ playerId, goals, assists, position, showAssists }) => {
  const playerData = usePlayer(playerId);
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const isFirst = position === 1;
  const total = goals + assists;

  return (
    <Flex
      align="center"
      gap="sm"
      py="xs"
      px="xs"
      style={{
        background: isFirst
          ? `linear-gradient(90deg, ${theme.colors.yellow[1]} 0%, transparent 100%)`
          : undefined,
        borderRadius: 8,
        cursor: "pointer",
      }}
      onClick={() => {
        if (playerData.isSuccess) {
          void navigate(`/teams/${playerData.data.teamId}`);
        }
      }}
    >
      {/* Position Badge */}
      <Flex
        align="center"
        justify="center"
        w={24}
        h={24}
        style={{
          borderRadius: 4,
          background: isFirst ? theme.colors.yellow[5] : theme.colors.gray[1],
          color: isFirst ? theme.white : theme.colors.gray[6],
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        {isFirst ? <IconTrophy size={12} /> : position}
      </Flex>

      {/* Team Logo */}
      {playerData.isSuccess && (
        <Box w={24}>
          <TeamLogo teamId={playerData.data.teamId} height={24} />
        </Box>
      )}

      {/* Player Name */}
      <Text
        size="sm"
        fw={isFirst ? 600 : 400}
        style={{ flex: 1, wordBreak: "break-all" }}
        lineClamp={1}
      >
        <PlayerName variant={"lastname, firstname"} playerId={playerId} /> (
        <PlayerNumber playerId={playerId} />)
      </Text>

      {/* Stats */}
      {showAssists ? (
        <>
          <Text size="sm" c="dimmed" w={24} ta="center">
            {goals}
          </Text>
          <Text size="sm" c="dimmed" w={24} ta="center">
            {assists}
          </Text>
          <Text
            fw={700}
            size="sm"
            c={isFirst ? "green.7" : undefined}
            w={24}
            ta="center"
          >
            {total}
          </Text>
        </>
      ) : (
        <Text
          fw={700}
          size="sm"
          c={isFirst ? "green.7" : undefined}
          w={24}
          ta="center"
        >
          {goals}
        </Text>
      )}
    </Flex>
  );
};

export const TopscorerHighlight: FC<TopscorerHighlightProps> = ({
  maxPlayers = 3,
}) => {
  const competitionId = useContext(CompetitionContext);
  const theme = useMantineTheme();

  const competition = useCompetition(competitionId);
  const topscorerData = useTopscorer(competitionId);

  if (!topscorerData.isSuccess) {
    return <CenteredLoader />;
  }

  const topscorer = topscorerData.data.topscorer.slice(0, maxPlayers);

  if (topscorer.length === 0) {
    return null;
  }

  const showAssists = competition.data?.settings.topscorerCountAssists ?? false;

  return (
    <CardContainer>
      <WidgetHeader
        icon={<IconBallFootball size={20} color={theme.colors.green[6]} />}
        title="Topscorer"
        linkText="Alle"
        linkTo="/topscorer"
      />

      {/* Header Row */}
      <Flex align="center" py={4} px="xs" gap="sm">
        <Box w={24} />
        <Box w={24} />
        <Text size="xs" c="dimmed" style={{ flex: 1 }}>
          Spieler
        </Text>
        {showAssists ? (
          <>
            <Text size="xs" c="dimmed" w={24} ta="center">
              T
            </Text>
            <Text size="xs" c="dimmed" w={24} ta="center">
              A
            </Text>
            <Text size="xs" c="dimmed" w={24} ta="center">
              S
            </Text>
          </>
        ) : (
          <Text size="xs" c="dimmed" w={24} ta="center">
            T
          </Text>
        )}
      </Flex>

      <Flex direction="column" gap={4}>
        {topscorer.map((scorer) => (
          <TopscorerCard
            key={scorer.playerId}
            playerId={scorer.playerId}
            goals={scorer.goals}
            assists={scorer.assists}
            position={scorer.position}
            showAssists={showAssists}
          />
        ))}
      </Flex>
    </CardContainer>
  );
};
